import { useEffect, useState } from 'react';

/**
 * Lightweight media-query hook. SSR-safe (returns the `defaultValue` until
 * the first client effect resolves the actual match). We use this for the
 * desktop-vs-mobile drawer split — CSS-only gating is not enough because
 * Vaul portals its overlay to `<body>` and would intercept clicks even
 * when visually hidden.
 */
export function useMediaQuery(query: string, defaultValue = false): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return defaultValue;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);

  return matches;
}
